import { useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { GET_BOTMAKER_SETTINGS } from '../../../gql/queries/botmaker'
import { Button, Form, Modal } from 'react-bootstrap'
import { CampaignChannel, IMessageTemplate } from '../../../types/campaigns'
import { SUPPORTED_CHANNELS } from '../../../constants/campaigns'
import { TemplateStarter } from '../../../constants/templateStarters'

type TemplateFormValues = {
  name: string
  description?: string
  channel: CampaignChannel
  subject?: string
  body: string
  variables?: string[]
  metadata?: Record<string, any>
  namespace?: string
  enabledChannels?: CampaignChannel[]
}

type Props = {
  show: boolean
  onHide: () => void
  onSubmit: (values: TemplateFormValues) => Promise<void>
  template?: IMessageTemplate | null
  starter?: TemplateStarter | null
  loading?: boolean
  defaultChannel?: CampaignChannel
}



const channelOptions = SUPPORTED_CHANNELS

export const TemplateFormModal = ({ show, onHide, onSubmit, template, starter, loading, defaultChannel }: Props) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [channel, setChannel] = useState<CampaignChannel>('EMAIL')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [variables, setVariables] = useState('')
  const [metadataJson, setMetadataJson] = useState('{}')
  const [namespace, setNamespace] = useState('')

  const { data: settingsData } = useQuery(GET_BOTMAKER_SETTINGS, {
    skip: channel !== 'WHATSAPP',
  })

  // Determine initial enabled channels from template
  const [enabledChannels, setEnabledChannels] = useState<CampaignChannel[]>([])

  useEffect(() => {
    if (settingsData?.botmakerSettings?.namespace && !template) {
      setNamespace(settingsData.botmakerSettings.namespace)
    }
  }, [settingsData, template])

  useEffect(() => {
    if (!template) {
      setName('')
      setDescription('')
      setChannel(defaultChannel || 'EMAIL')
      setSubject('')
      setBody('')
      setVariables('')
      setMetadataJson('{}')
      setNamespace('')
      setEnabledChannels(['EMAIL']) // Default for new templates
      return
    }
    setName(template.name)
    setDescription(template.description ?? '')
    setChannel(template.channel)
    setSubject(template.subject ?? '')
    setBody(template.body)
    setVariables(
      Array.isArray(template.variables)
        ? template.variables.join(', ')
        : (template.variables || '')
    )
    // Parse metadata if it's a string (which can happen with some GQL/Django setups)
    let meta = template.metadata
    if (typeof meta === 'string') {
      try {
        meta = JSON.parse(meta)
      } catch (e) {
        console.error("Failed to parse metadata JSON", e)
        meta = {}
      }
    }

    setMetadataJson(JSON.stringify(meta ?? {}, null, 2))
    setNamespace(meta?.namespace || '')

    // Check metadata for enabled channels, fallback to legacy channel or just EMAIL
    const metaChannels = meta?.enabled_channels
    if (Array.isArray(metaChannels)) {
      setEnabledChannels(metaChannels)
    } else {
      setEnabledChannels(template.channel ? [template.channel] : ['EMAIL'])
    }
  }, [template, defaultChannel])

  // Pre-fill form with starter content
  useEffect(() => {
    if (starter && !template) {
      setName(starter.name)
      setDescription(starter.description)
      setChannel(starter.channel)
      setSubject(starter.content.subject)
      setBody(starter.content.bodyHtml)
      setVariables(starter.content.variables.join(', '))
      setEnabledChannels([starter.channel])
    }
  }, [starter, template])

  const handleChannelToggle = (ch: CampaignChannel) => {
    setEnabledChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    )
  }

  const handleSubmit = async () => {
    const payload: TemplateFormValues = {
      name,
      description,
      channel: 'EMAIL', // Legacy placeholder
      subject,
      body: '',
      variables: [],
      enabledChannels, // Explicit argument for backend mutation
      metadata: {
        // Store enabled channels in metadata so we can read it back
        enabled_channels: enabledChannels,
        namespace // Preserve namespace if set
      },
    }
    // NOTE: actually creating the variants on backend when 'enabledChannels' are present is a separate step.
    // If the backend 'CreateMessageTemplate' doesn't handle creation of variants, we might need to do it here
    // or update the backend.
    // The user said "then these should be the tabs visible".
    // So we just store preference.
    await onSubmit(payload)
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{template ? 'Edit template' : 'Create template'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className='mb-4'>
          <Form.Label>Name</Form.Label>
          <Form.Control
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder='e.g. Renewal Reminder'
            autoFocus
          />
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label>Enabled Channels</Form.Label>
          <div className='d-flex gap-3 flex-wrap'>
            {channelOptions.map(opt => (
              <Form.Check
                key={opt}
                type="checkbox"
                id={`channel-${opt}`}
                label={opt}
                checked={enabledChannels.includes(opt)}
                onChange={() => handleChannelToggle(opt)}
              />
            ))}
          </div>
          <Form.Text muted>Select which channels you want to configure for this template.</Form.Text>
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label>Subject</Form.Label>
          <Form.Control
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder='e.g. Important notice about your policy'
          />
          <Form.Text muted>
            Default subject for email variants.
          </Form.Text>
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label>Description</Form.Label>
          <Form.Control
            as='textarea'
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSubmit} disabled={loading || !name}>
          {template ? 'Save' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
